// ---------------------------------------------------------------------------
// The wizard's steps and their rules (R17b)
//
// Which steps there are, when one is done, what holds a person on one, how
// far the stepper lets them jump, and what a click on Next or on a step does.
// Taken out of pages/campaign-wizard.ts unchanged, so that the frame's
// rebuild (R17c) redoes only the markup. Plain TypeScript: no Lit, no
// Adornis, nothing that renders or fetches; the page hands in its state.
// ---------------------------------------------------------------------------

import type { CampaignDraft } from '../types.js';
import { salesRepsWithoutAnyUsableAccount } from '../types.js';
import { LINKEDIN_CONNECT_NOTE_MAX_CHARS } from './sequence.js';

/** The six steps, in order. The page adds an icon to each. */
export const WIZARD_STEPS = [
  { key: 'leads', label: 'Leads' },
  { key: 'offering', label: 'Offering' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'type', label: 'Sales Representatives' },
  { key: 'sequence', label: 'Sequence' },
  { key: 'summary', label: 'Summary' },
] as const;

export type WizardStepKey = (typeof WIZARD_STEPS)[number]['key'];

/**
 * Steps built anew each time they are entered. The Summary asks to confirm
 * the coverage gaps of the campaign as it is then; a confirmation kept from
 * an earlier look would cover gaps changed since. Sales Representatives holds
 * nothing the person picked (every pick is in the draft), only the reps and
 * their sending accounts as the server listed them: entering it again reads
 * them again, so an account connected in Settings meanwhile shows and a list
 * that failed to load (its error has no "Try again") is tried again. Kept, it
 * showed both as they were until the person left the wizard (R17a review).
 */
export const REBUILT_ON_ENTRY: ReadonlySet<WizardStepKey> = new Set(['type', 'summary']);

/**
 * The steps on the page, in step order: every step shown in this visit, kept
 * while another one is shown (R17a). Rendering only the current one, inside
 * `keyed(currentStep, …)`, threw a step away on every step change, and with
 * it whatever it held that the draft does not — the chosen lead source, the
 * pages "Load 25 more" added, a lead's details already paid for, a talking
 * point being typed, a message being edited — and the lead step ran its saved
 * search again on every return. A step in REBUILT_ON_ENTRY leaves the page
 * when another one is shown. `visited` is this visit's own set; it is updated.
 */
export const keptSteps = (visited: Set<number>, current: number): number[] => {
  visited.add(current);
  for (const step of visited)
    if (step !== current && REBUILT_ON_ENTRY.has(WIZARD_STEPS[step]!.key)) visited.delete(step);
  return [...visited].sort((a, b) => a - b);
};

/** Whether a wizard step is complete, from the draft alone. The Summary is complete once the wizard finished. */
export const isStepComplete = (stepKey: WizardStepKey, d: CampaignDraft, isFinished = false): boolean => {
  switch (stepKey) {
    case 'type': {
      if (!d.aiEmployeeId) return false;
      // Sales Rep shares must total exactly 100%
      const totalPct = d.aiEmployeeAssignments.reduce((s, a) => s + (a.percentage || 0), 0);
      if (d.aiEmployeeAssignments.length === 0 || totalPct !== 100) return false;
      // We don't know yet which channels the user will pick in the Sequence
      // step, so the only hard requirement here is that *some* account is
      // configured — either email or LinkedIn. Per-channel completeness is
      // re-validated on the Launch step against the actual sequence.
      return d.emailAccounts.length > 0 || d.linkedinAccountIds.length > 0;
    }
    case 'offering':
      // Only the product/service name is required. Description and talking
      // points add context but are optional, so the step is "done" as soon as
      // the name is filled in (matches stepBlockReason's gate).
      return !!d.offering.name.trim();
    case 'leads':
      return (d.leads.length > 0 || !!d.apolloSearchInput) && !!d.name.trim();
    case 'schedule':
      return d.schedule.sendDays.length > 0;
    case 'sequence':
      return d.sequence.length > 0;
    case 'summary':
      return isFinished;
  }
};

/**
 * The Sales Rep step's "no sending account picked yet" gate — the one step
 * gate that stays quiet until the user actually tries to move on.
 *
 * Switching the Sales Rep on a cooling-down campaign used to greet you with a
 * warning banner the instant you ticked the new rep, and then an error when
 * you carried on — two messages for one thing you were already on your way to
 * doing. Now the rep picker carries a single quiet inline hint on the rep
 * that's missing an account, and this sentence is what you get if you push
 * forward anyway. Compared by identity (stepMove, navigation) to keep the loud
 * bottom banner (correct for every other gate) off this one.
 */
export const SALES_REP_ACCOUNT_BLOCK = 'Please select an email or LinkedIn account.';

/**
 * Required-field gate for a step: a human-readable reason why this step's
 * required fields aren't satisfied yet (so the user can't advance past it), or
 * null when the step is good to leave. One source of truth for the "Next"
 * button, the inline hint, the stepper dots and the step-move guard.
 *
 * `live` is the draft the reps' coverage is checked on: the page passes its
 * latest draft there (liveDraft.current), and the draft it rendered with as
 * `draft`, as it always did.
 */
export const stepBlockReason = (
  key: WizardStepKey | undefined,
  draft: CampaignDraft,
  offeringBusy: boolean,
  live: CampaignDraft = draft,
): string | null => {
  if (key === 'leads') {
    if (!draft.name.trim()) {
      return 'Enter a campaign name to continue.';
    }
  }
  if (key === 'offering') {
    if (offeringBusy) {
      return 'Please wait until the document/website finishes processing.';
    }
    if (!draft.offering.name.trim()) {
      return 'Enter a product or service name to continue.';
    }
  }
  if (key === 'type') {
    if (draft.aiEmployeeAssignments.length === 0) {
      return 'Select at least one Sales Representative to continue.';
    }
    const totalPct = draft.aiEmployeeAssignments.reduce((s, a) => s + (a.percentage || 0), 0);
    if (totalPct !== 100) {
      return 'Sales Representative shares must total 100% before continuing.';
    }
    // Hard-gate only a rep that can send on NO channel the sequence uses (no
    // account at all). A rep missing just ONE channel is a coverage gap the
    // Summary step resolves — borrow another rep's account or acknowledge the
    // skip — so it is let through here. Blocking those trapped the user before
    // they could ever reach that resolution.
    if (salesRepsWithoutAnyUsableAccount(live).length > 0) {
      return SALES_REP_ACCOUNT_BLOCK;
    }
    // The floor, for a draft whose sequence has no steps yet: no channel is
    // "required" so the check above can't fire, but nothing can be sent from
    // a campaign with no account at all. Stays a hard gate with the banner —
    // there is no inline hint in this case to soft-gate behind.
    if (draft.emailAccounts.length === 0 && draft.linkedinAccountIds.length === 0) {
      return 'Connect and select at least one email or LinkedIn account for your Sales Representative before continuing.';
    }
  }
  if (key === 'sequence') {
    if (draft.sequence.length === 0) {
      return 'Add at least one sequence step to continue.';
    }
    // Same rule as the Sales Rep step: only a rep that can serve NO required
    // channel blocks here. A coverage gap (one channel missing) is resolved on
    // the Summary step's Channel Coverage Warning, not blocked before it.
    if (salesRepsWithoutAnyUsableAccount(live).length > 0) {
      return SALES_REP_ACCOUNT_BLOCK;
    }
    const overLongConnect = draft.sequence.some(
      s => s.channel === 'linkedin_connect' && (s.manualBody?.length ?? 0) > LINKEDIN_CONNECT_NOTE_MAX_CHARS,
    );
    if (overLongConnect) {
      return `Shorten the connection request message to ${LINKEDIN_CONNECT_NOTE_MAX_CHARS} characters or fewer — LinkedIn rejects longer invitations.`;
    }
  }
  return null;
};

/** A step's block reason, by its index (the page binds its draft and busy state). */
export type ReasonAt = (step: number) => string | null;

/**
 * Furthest step the user may jump to: walk forward from the current step and
 * stop at the first one whose required fields aren't satisfied yet. The user
 * can still land ON that step (to fill it) but not skip past it — so the
 * stepper dots beyond it are disabled.
 */
export const forwardLimit = (currentStep: number, reasonAt: ReasonAt): number => {
  let limit = currentStep;
  while (limit < WIZARD_STEPS.length - 1 && !reasonAt(limit)) limit++;
  return limit;
};

/** What a click on Next, Back or a stepper dot does. */
export type StepMove =
  /** Nothing: the Offering step is still parsing or analysing. */
  | { kind: 'stay' }
  /** Refused with this reason (a toast; the soft Sales Rep gate also reveals its per-rep hints). */
  | { kind: 'refuse'; reason: string }
  /** Goes; `saveFirst` when the lead step with a search is left forwards (the draft is saved before). */
  | { kind: 'go'; saveFirst: boolean };

export const stepMove = (args: {
  from: number;
  to: number;
  offeringBusy: boolean;
  hasApolloSearch: boolean;
  reasonAt: ReasonAt;
}): StepMove => {
  const { from, to, offeringBusy, hasApolloSearch, reasonAt } = args;
  // Block ALL navigation while the Offering step is still parsing/analyzing —
  // leaving now would throw away the in-progress result.
  if (offeringBusy && WIZARD_STEPS[from]?.key === 'offering') return { kind: 'stay' };
  // Gate forward navigation: every step we'd move past must have its required
  // fields filled. Covers the Next button AND direct stepper-dot jumps.
  if (to > from) {
    for (let i = from; i < to; i++) {
      const reason = reasonAt(i);
      // Only a soft-gated step ever gets here — every other gate disables
      // "Next" outright, so there is no click to answer. This is the error the
      // Sales Rep step's quiet inline hint escalates to once the user actually
      // tries to move on without picking an account. That attempt is also what
      // first reveals those per-rep hints (they are held back until now, so a
      // rep just ticked is not marked before they can act).
      if (reason) return { kind: 'refuse', reason };
    }
  }
  return { kind: 'go', saveFirst: to > from && WIZARD_STEPS[from]?.key === 'leads' && hasApolloSearch };
};

/** One dot of the stepper, as the page draws it. */
export interface StepperItem {
  key: WizardStepKey;
  label: string;
  current: boolean;
  /** Done and reached (or the wizard finished): drawn with a tick. */
  complete: boolean;
  /** The line before this dot is drawn as done (always false for the first). */
  lineDone: boolean;
  disabled: boolean;
  /** Why the dot is disabled, or ''. */
  title: string;
}

/**
 * The stepper: a step shows a tick only if the user has reached it (or the
 * wizard finished); dots past the forward limit are disabled with the reason
 * of the step that holds them back; while the Offering step is busy, all are.
 * While the steps are `held` (an edited campaign not loaded, logic/load.ts
 * stepsHeldBy), all are, with that reason, and no step's own rule (R17a3
 * review): nothing is ticked or drawn as done either, for the empty draft's
 * default schedule and placeholder step meet two rules, not the campaign
 * (found in Chrome).
 */
export const stepperItems = (args: {
  draft: CampaignDraft;
  currentStep: number;
  maxStepReached: number;
  finished: boolean;
  offeringBusy: boolean;
  reasonAt: ReasonAt;
  held?: string | null;
}): StepperItem[] => {
  const { draft, currentStep, maxStepReached, finished, offeringBusy, reasonAt, held } = args;
  const limit = forwardLimit(currentStep, reasonAt);
  return WIZARD_STEPS.map((step, i) => ({
    key: step.key,
    label: step.label,
    current: i === currentStep,
    complete: !held && isStepComplete(step.key, draft, finished) && (i <= maxStepReached || finished),
    lineDone:
      !held &&
      i > 0 &&
      ((isStepComplete(WIZARD_STEPS[i - 1]!.key, draft, finished) && (i - 1 < maxStepReached || finished)) ||
        i <= currentStep),
    disabled: !!held || offeringBusy || i > limit,
    title: held
      ? held
      : offeringBusy
        ? 'Please wait until processing finishes'
        : i > limit
          ? (reasonAt(limit) ?? 'Complete the required fields to continue')
          : '',
  }));
};

/** What sits under the current step and in the Back / Next row. */
export interface StepNavigation {
  canGoBack: boolean;
  canGoForward: boolean;
  /** The current step's block reason, or null. */
  blockReason: string | null;
  /** The reason is the soft Sales Rep gate: Next stays clickable and answers on the click. */
  softBlocked: boolean;
  /** The banner under the step: the Offering step's processing note, a block reason, or none. */
  banner: { kind: 'busy' } | { kind: 'blocked'; reason: string } | null;
  backLabel: string;
  nextLabel: string;
}

/**
 * Back, Next and the banner for the current step. While the steps are `held`
 * (an edited campaign not loaded, logic/load.ts stepsHeldBy) there is none of
 * them: the step on the page is the spinner or the failure card, not a step
 * whose rules could be met (R17a3 review).
 */
export const stepNavigation = (
  currentStep: number,
  offeringBusy: boolean,
  reasonAt: ReasonAt,
  held: string | null = null,
): StepNavigation => {
  if (held)
    return {
      canGoBack: false,
      canGoForward: false,
      blockReason: null,
      softBlocked: false,
      banner: null,
      backLabel: '',
      nextLabel: '',
    };
  const canGoForward = currentStep < WIZARD_STEPS.length - 1;
  const blockReason = reasonAt(currentStep);
  const softBlocked = blockReason === SALES_REP_ACCOUNT_BLOCK;
  return {
    canGoBack: currentStep > 0,
    canGoForward,
    blockReason,
    softBlocked,
    banner: offeringBusy
      ? { kind: 'busy' }
      : blockReason && !softBlocked && canGoForward
        ? { kind: 'blocked', reason: blockReason }
        : null,
    backLabel: WIZARD_STEPS[currentStep - 1]?.label ?? '',
    nextLabel: WIZARD_STEPS[currentStep + 1]?.label ?? '',
  };
};
